import type {
  TranscriptSegment,
  VideoIndexedMediaResponse,
  VideoMarker,
  VideoPlanItem,
} from "@friends-church-dvr/contracts";

export interface GroupedTranscriptItem {
  id: string;
  title: string;
  itemType: string | null;
  startMs: number;
  confidence: number | null;
  segments: TranscriptSegment[];
  sourceSegments: TranscriptSegment[];
}

export interface GroupedTranscriptSection {
  title: string;
  confidence: number | null;
  items: GroupedTranscriptItem[];
}

interface PlanItemContext {
  item: VideoPlanItem;
  sectionTitle: string;
}

interface TranscriptFragment {
  segment: TranscriptSegment;
  marker: VideoMarker | null;
}

interface TranscriptBoundaryCandidate {
  timestampMs: number;
  segmentId: string;
  textIndex: number;
  confidence: number;
  pauseMs: number;
  closesThought: boolean;
}

interface SelectedTranscriptBoundary extends TranscriptBoundaryCandidate {
  marker: VideoMarker;
}

export const transcriptBoundaryPaddingMs = 20_000;
export const postServiceSilenceCutoffMs = 60_000;

const normalizedType = (value: string | null): string => value?.trim().toLowerCase() ?? "";

const normalizedTitle = (value: string | null): string =>
  value?.trim().toLocaleLowerCase("en-US").replace(/[\s-]+/gu, "_") ?? "";

const isBumperTitle = (value: string | null): boolean =>
  /(^|_)bumper(?:_|$)/u.test(normalizedTitle(value));

const orderedPlanItems = (items: VideoPlanItem[]): VideoPlanItem[] =>
  [...items].sort((left, right) =>
    (left.sequence ?? Number.MAX_SAFE_INTEGER) - (right.sequence ?? Number.MAX_SAFE_INTEGER),
  );

const planContexts = (items: VideoPlanItem[]): Map<string, PlanItemContext> => {
  const contexts = new Map<string, PlanItemContext>();
  let sectionTitle = "Service";
  for (const item of orderedPlanItems(items)) {
    if (normalizedType(item.itemType) === "header") {
      sectionTitle = item.title;
      continue;
    }
    contexts.set(item.id, { item, sectionTitle });
  }
  return contexts;
};

const pcoMarkers = (markers: VideoMarker[]): VideoMarker[] =>
  markers
    .filter((marker) => marker.pcoPlanItemId !== null)
    .sort((left, right) => left.startMs - right.startMs);

const markerContext = (
  marker: VideoMarker | undefined,
  contexts: Map<string, PlanItemContext>,
): PlanItemContext | null => marker?.pcoPlanItemId
  ? contexts.get(marker.pcoPlanItemId) ?? null
  : null;

const isSongMarker = (
  marker: VideoMarker | undefined,
  contexts: Map<string, PlanItemContext>,
): boolean => normalizedType(markerContext(marker, contexts)?.item.itemType ?? null) === "song";

const isBumperMarker = (
  marker: VideoMarker | undefined,
  contexts: Map<string, PlanItemContext>,
): boolean => {
  if (!marker) return false;
  const context = markerContext(marker, contexts);
  return isBumperTitle(context?.item.title ?? marker.pcoPlanItemTitle ?? marker.label);
};

const isNonTranscriptMarker = (
  marker: VideoMarker | undefined,
  contexts: Map<string, PlanItemContext>,
): boolean => isSongMarker(marker, contexts) || isBumperMarker(marker, contexts);

const markerForSegment = (
  segment: TranscriptSegment,
  markers: VideoMarker[],
  contexts: Map<string, PlanItemContext>,
): VideoMarker | null => {
  const midpoint = segment.startMs + (segment.endMs - segment.startMs) / 2;
  let markerIndex = -1;
  for (let index = 0; index < markers.length; index += 1) {
    if (markers[index].startMs > midpoint) break;
    markerIndex = index;
  }
  if (markerIndex < 0) {
    const first = markers[0];
    return first && !isNonTranscriptMarker(first, contexts) && midpoint >= first.startMs - transcriptBoundaryPaddingMs
      ? first
      : null;
  }
  const marker = markers[markerIndex];
  if (isSongMarker(marker, contexts)) {
    const next = markers.slice(markerIndex + 1)
      .find((candidate) => !isNonTranscriptMarker(candidate, contexts));
    return next && midpoint >= next.startMs - transcriptBoundaryPaddingMs
      ? next
      : marker;
  }
  if (isBumperMarker(marker, contexts)) {
    return markers.slice(0, markerIndex).reverse()
      .find((candidate) => !isNonTranscriptMarker(candidate, contexts)) ?? null;
  }
  return marker.endMs === null || midpoint <= marker.endMs + transcriptBoundaryPaddingMs ? marker : null;
};

const estimatedTextTimestamp = (segment: TranscriptSegment, textIndex: number): number => {
  const totalWords = segment.text.match(/\S+/gu)?.length ?? 0;
  const precedingWords = segment.text.slice(0, textIndex).match(/\S+/gu)?.length ?? 0;
  const progress = totalWords > 0
    ? precedingWords / totalWords
    : textIndex / Math.max(1, segment.text.length);
  return Math.round(segment.startMs + (segment.endMs - segment.startMs) * progress);
};

const closesThought = (text: string): boolean =>
  /(?:\bamen|\bthank\s+you|\bwe\s+love\s+you|\bsee\s+you\s+soon)[.!?\s”"')]*$/iu.test(text.trim());

const transcriptBoundaryCandidates = (
  segments: TranscriptSegment[],
): TranscriptBoundaryCandidate[] => {
  const ordered = [...segments].sort((left, right) => left.startMs - right.startMs);
  let previousEndMs: number | null = null;
  return ordered.flatMap((segment) => {
    const confidence = segment.confidence === null
      ? 0.7
      : Math.min(1, Math.max(0, segment.confidence));
    const pauseMs = previousEndMs === null ? 0 : Math.max(0, segment.startMs - previousEndMs);
    previousEndMs = previousEndMs === null ? segment.endMs : Math.max(previousEndMs, segment.endMs);
    const candidates: TranscriptBoundaryCandidate[] = [{
      timestampMs: segment.startMs,
      segmentId: segment.id,
      textIndex: 0,
      confidence,
      pauseMs,
      closesThought: false,
    }];
    for (const match of segment.text.matchAll(/[.!?]+[”"')]*\s+/gu)) {
      const textIndex = (match.index ?? 0) + match[0].length;
      if (textIndex >= segment.text.length) continue;
      candidates.push({
        timestampMs: estimatedTextTimestamp(segment, textIndex),
        segmentId: segment.id,
        textIndex,
        confidence,
        pauseMs: 0,
        closesThought: closesThought(segment.text.slice(0, textIndex)),
      });
    }
    return candidates;
  });
};

const selectedTranscriptBoundaries = (
  segments: TranscriptSegment[],
  markers: VideoMarker[],
  contexts: Map<string, PlanItemContext>,
): SelectedTranscriptBoundary[] => {
  const candidates = transcriptBoundaryCandidates(segments);
  const selected: SelectedTranscriptBoundary[] = [];
  for (let index = 0; index < markers.length; index += 1) {
    const marker = markers[index];
    if (isNonTranscriptMarker(marker, contexts)) continue;
    const minimumMs = index === 0
      ? Math.max(0, marker.startMs - transcriptBoundaryPaddingMs)
      : Math.max(marker.startMs - transcriptBoundaryPaddingMs, selected.at(-1)?.timestampMs ?? 0);
    const nextMarker = markers[index + 1];
    const maximumMs = Math.min(
      marker.startMs + transcriptBoundaryPaddingMs,
      nextMarker?.startMs ?? Number.MAX_SAFE_INTEGER,
    );
    const best = candidates
      .filter(({ timestampMs }) => timestampMs >= minimumMs && timestampMs <= maximumMs)
      .map((candidate) => {
        const closeness = 1 - Math.abs(candidate.timestampMs - marker.startMs) /
          transcriptBoundaryPaddingMs;
        const pauseQuality = Math.min(1, candidate.pauseMs / 3_000);
        const score = closeness * 0.55 + 0.2 + pauseQuality * 0.1 +
          candidate.confidence * 0.05 + (candidate.closesThought ? 0.1 : 0);
        return { candidate, score };
      })
      .sort((left, right) => right.score - left.score ||
        Math.abs(left.candidate.timestampMs - marker.startMs) -
        Math.abs(right.candidate.timestampMs - marker.startMs))[0]?.candidate;
    if (best) selected.push({ ...best, marker });
  }
  return selected;
};

const transcriptFragments = (
  segment: TranscriptSegment,
  boundaries: SelectedTranscriptBoundary[],
  markers: VideoMarker[],
  contexts: Map<string, PlanItemContext>,
): TranscriptFragment[] => {
  const splits = boundaries
    .filter((boundary) => boundary.segmentId === segment.id)
    .filter((boundary) => boundary.textIndex > 0 && boundary.textIndex < segment.text.length)
    .sort((left, right) => left.textIndex - right.textIndex);
  if (splits.length === 0) {
    return [{ segment, marker: markerForSegment(segment, markers, contexts) }];
  }
  const fragments: TranscriptFragment[] = [];
  let textIndex = 0;
  let startMs = segment.startMs;
  for (const split of splits) {
    const text = segment.text.slice(textIndex, split.textIndex).trim();
    if (text) fragments.push({
      marker: markerForSegment({ ...segment, startMs, endMs: split.timestampMs }, markers, contexts),
      segment: {
        ...segment,
        id: `${segment.id}:before-${split.marker.id}`,
        startMs,
        endMs: split.timestampMs,
        text,
      },
    });
    textIndex = split.textIndex;
    startMs = split.timestampMs;
  }
  const text = segment.text.slice(textIndex).trim();
  if (text) fragments.push({
    marker: markerForSegment({ ...segment, startMs }, markers, contexts),
    segment: {
      ...segment,
      id: `${segment.id}:after-${splits.at(-1)?.marker.id ?? "boundary"}`,
      startMs,
      text,
    },
  });
  return fragments;
};

const hasServiceClosingCue = (text: string): boolean =>
  /\b(?:thank\s+you|we\s+love\s+you|we(?:'ll|\s+will)\s+see\s+you\s+soon|have\s+a\s+(?:great|wonderful|blessed)\s+(?:day|week))\b/iu.test(text);

const transcriptDisplaySegments = (
  segments: TranscriptSegment[],
  markers: VideoMarker[],
  contexts: Map<string, PlanItemContext>,
): TranscriptSegment[] => {
  const ordered = [...segments].sort((left, right) => left.startMs - right.startMs);
  const lastSpokenMarker = [...markers].reverse()
    .find((marker) => !isNonTranscriptMarker(marker, contexts));
  if (!lastSpokenMarker) return ordered;

  let finalClosingIndex = -1;
  for (let index = 0; index < ordered.length - 1; index += 1) {
    const segment = ordered[index];
    if (segment.endMs < lastSpokenMarker.startMs || !hasServiceClosingCue(segment.text)) continue;
    const later = ordered.slice(index + 1);
    const laterHasContinuousSpeech = later.some((candidate, laterIndex) => {
      const following = later[laterIndex + 1];
      return following && following.startMs - candidate.endMs < 30_000;
    });
    if (!laterHasContinuousSpeech &&
        ordered[index + 1].startMs - segment.endMs >= postServiceSilenceCutoffMs) {
      finalClosingIndex = index;
    }
  }
  return finalClosingIndex < 0 ? ordered : ordered.slice(0, finalClosingIndex + 1);
};

export const aggregateTranscriptConfidence = (
  segments: TranscriptSegment[],
): number | null => {
  let weightedTotal = 0;
  let totalWeight = 0;
  for (const segment of segments) {
    if (segment.confidence === null || !Number.isFinite(segment.confidence)) continue;
    const weight = Math.max(1, segment.text.trim().split(/\s+/u).filter(Boolean).length);
    weightedTotal += Math.min(1, Math.max(0, segment.confidence)) * weight;
    totalWeight += weight;
  }
  return totalWeight > 0 ? weightedTotal / totalWeight : null;
};

export const groupTranscriptByPlan = (
  payload: VideoIndexedMediaResponse,
): GroupedTranscriptSection[] => {
  const sourceSegments = payload.transcript?.segments ?? [];
  if (sourceSegments.length === 0) return [];

  const markers = pcoMarkers(payload.markers);
  if (markers.length === 0) {
    return [{
      title: "Transcript",
      confidence: aggregateTranscriptConfidence(sourceSegments),
      items: [{
        id: "unplanned-spoken-content",
        title: "Spoken Content",
        itemType: null,
        startMs: sourceSegments[0].startMs,
        confidence: aggregateTranscriptConfidence(sourceSegments),
        segments: sourceSegments,
        sourceSegments,
      }],
    }];
  }

  const contexts = planContexts(payload.planItems);
  const segments = transcriptDisplaySegments(sourceSegments, markers, contexts);
  const boundaries = selectedTranscriptBoundaries(segments, markers, contexts);
  const boundaryByMarker = new Map(boundaries.map((boundary) => [boundary.marker.id, boundary]));
  const originalMarkerById = new Map(markers.map((marker) => [marker.id, marker]));
  const displayMarkers = markers.map((marker) => {
    const boundary = boundaryByMarker.get(marker.id);
    return boundary ? { ...marker, startMs: boundary.timestampMs } : marker;
  });
  const sections = new Map<string, GroupedTranscriptSection>();
  const items = new Map<string, GroupedTranscriptItem>();

  const itemForMarker = (marker: VideoMarker): GroupedTranscriptItem | null => {
    if (!marker.pcoPlanItemId) return null;
    const context = contexts.get(marker.pcoPlanItemId);
    if (isNonTranscriptMarker(marker, contexts)) return null;
    const sectionTitle = context?.sectionTitle ?? "Service";
    let section = sections.get(sectionTitle);
    if (!section) {
      section = { title: sectionTitle, confidence: null, items: [] };
      sections.set(sectionTitle, section);
    }
    let item = items.get(marker.pcoPlanItemId);
    if (!item) {
      item = {
        id: marker.pcoPlanItemId,
        title: context?.item.title ?? marker.pcoPlanItemTitle ?? marker.label,
        itemType: context?.item.itemType ?? null,
        startMs: originalMarkerById.get(marker.id)?.startMs ?? marker.startMs,
        confidence: null,
        segments: [],
        sourceSegments: [],
      };
      items.set(marker.pcoPlanItemId, item);
      section.items.push(item);
    }
    return item;
  };

  for (const segment of segments) {
    const fragments = transcriptFragments(
      segment,
      boundaries,
      displayMarkers,
      contexts,
    );
    for (const fragment of fragments) {
      if (!fragment.marker) continue;
      itemForMarker(fragment.marker)?.segments.push(fragment.segment);
    }
    const sourceMarker = markerForSegment(segment, displayMarkers, contexts);
    if (sourceMarker) itemForMarker(sourceMarker)?.sourceSegments.push(segment);
  }

  return [...sections.values()]
    .filter((section) => section.items.length > 0)
    .map((section) => {
      for (const item of section.items) item.confidence = aggregateTranscriptConfidence(item.segments);
      section.confidence = aggregateTranscriptConfidence(section.items.flatMap((item) => item.segments));
      return section;
    });
};
